import { arrayMove } from "@dnd-kit/sortable";
import type { GridValidRowModel } from "@mui/x-data-grid";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../../contexts/AuthProvider";
import { localStorageService } from "../../../../services/LocalStorageService";
import { resolveColumnDisplayOrder } from "./columnOrderUtils";
import type { SystemGridColDef } from "./types";

interface UseColumnOrderArgs<T extends GridValidRowModel> {
	enabled: boolean;
	localStorageKey: string | undefined;
	columns: SystemGridColDef<T>[];
	/** Fires with the new field order whenever it changes (move/reset/async restore) so callers can react. */
	onOrderChange?: (fieldOrder: string[]) => void;
}

interface UseColumnOrderResult<T extends GridValidRowModel> {
	orderedColumns: SystemGridColDef<T>[];
	/** Field ids in display order — feed straight to dnd-kit's `SortableContext`. */
	fieldIds: string[];
	/** True when a non-default order is active; drives the reset affordance. */
	isCustomized: boolean;
	/** Move `activeField` into `overField`'s slot, then persist the new order. */
	moveField: (activeField: string, overField: string) => void;
	/** Replace the whole display order (full field list, pinned included) and persist it exactly like a drag. */
	applyOrder: (fieldOrder: string[]) => void;
	/** Drop the custom order, revert to the columns' natural order, and clear storage. */
	resetOrder: () => void;
}

/**
 * Owns optional, persisted column ordering for SystemDataGrid. The order is stored as a list of
 * column `field` names (per-user, keyed by the table's `localStorageKey`). It reconciles gracefully
 * when the column set changes: saved-but-removed fields are dropped, and a column absent from the
 * saved order is slotted right after its nearest natural-order predecessor (see
 * `resolveReorderableSequence`), so a later column addition is never hidden and stays in its band.
 *
 * When `enabled` is false this is a no-op passthrough — `orderedColumns` is the exact same array
 * reference as `columns`, so non-reordering grids keep their existing render identity.
 */
export function useColumnOrder<T extends GridValidRowModel>({
	enabled,
	localStorageKey,
	columns,
	onOrderChange,
}: UseColumnOrderArgs<T>): UseColumnOrderResult<T> {
	const { userInfo } = useAuth();
	const userId = userInfo?.id ?? "";

	const [fieldOrder, setFieldOrder] = useState<string[]>(() => {
		if (!enabled || !localStorageKey || !userId) return [];
		return localStorageService.getTableColumnOrder(localStorageKey, userId) ?? [];
	});

	// `useAuth` populates userInfo asynchronously; if userId was empty at mount, retry the restore
	// once it arrives (mirrors useMultiSort). No-op if nothing was saved.
	const initialUserIdHadValueRef = useRef(!!userId);
	useEffect(() => {
		if (initialUserIdHadValueRef.current || !userId || !enabled || !localStorageKey) return;
		initialUserIdHadValueRef.current = true;
		const stored = localStorageService.getTableColumnOrder(localStorageKey, userId);
		if (stored) {
			setFieldOrder(stored);
			onOrderChange?.(stored);
		}
	}, [userId, enabled, localStorageKey, onOrderChange]);

	const orderedColumns = useMemo<SystemGridColDef<T>[]>(() => {
		if (!enabled || fieldOrder.length === 0) return columns;
		const byField = new Map(columns.map((c) => [String(c.field), c]));
		const pinned = new Set(columns.filter((c) => c.disableReorder).map((c) => String(c.field)));
		const displayOrder = resolveColumnDisplayOrder(
			fieldOrder,
			columns.map((c) => String(c.field)),
			pinned,
		);
		return columns.map((col, index) => byField.get(displayOrder[index]) ?? col);
	}, [enabled, columns, fieldOrder]);

	const fieldIds = useMemo(() => orderedColumns.map((c) => String(c.field)), [orderedColumns]);

	const applyOrder = useCallback(
		(next: string[]) => {
			if (!enabled) return;
			setFieldOrder(next);
			onOrderChange?.(next);
			if (localStorageKey && userId) localStorageService.saveTableColumnOrder(localStorageKey, next, userId);
		},
		[enabled, localStorageKey, userId, onOrderChange],
	);

	const moveField = useCallback(
		(activeField: string, overField: string) => {
			const from = fieldIds.indexOf(activeField);
			const to = fieldIds.indexOf(overField);
			if (from === -1 || to === -1 || from === to) return;
			applyOrder(arrayMove(fieldIds, from, to));
		},
		[fieldIds, applyOrder],
	);

	const resetOrder = useCallback(() => {
		setFieldOrder([]);
		onOrderChange?.([]);
		if (enabled && localStorageKey && userId) localStorageService.removeTableColumnOrder(localStorageKey, userId);
	}, [enabled, localStorageKey, userId, onOrderChange]);

	return { orderedColumns, fieldIds, isCustomized: enabled && fieldOrder.length > 0, moveField, applyOrder, resetOrder };
}
